import { UserRole, USER_ROLES } from './roles.js';

/**
 * Permission matrix for role-based access control
 * Defines what each role can view and edit
 */
export interface PermissionMatrix {
  view: 'all' | 'assigned_by_client' | 'read_only_scoped';
  editWorkflowFields: boolean;
  createNotes: boolean;
  manageUsers: boolean;
  manageCampaignSync: boolean;
  editableFields?: string[];
}

/**
 * Permissions for each role
 */
export const PERMISSIONS: Record<UserRole, PermissionMatrix> = {
  [USER_ROLES.ADMIN]: {
    view: 'all',
    editWorkflowFields: true,
    createNotes: true,
    manageUsers: true,
    manageCampaignSync: true,
    editableFields: ['im_comment', 'sdr_status', 'sdr_last_contacted_at', 'sdr_comment', 'sdr_owner_user_id', 'priority', 'next_action', 'due_at', 'call_feedback', 'meeting_date', 'meeting_time', 'meeting_timezone'],
  },
  [USER_ROLES.ADMIN_ASSISTANT]: {
    view: 'all', // See all leads (like ADMIN)
    editWorkflowFields: false, // Cannot edit workflow fields
    createNotes: true, // Can create notes (enabled for admin assistants)
    manageUsers: false, // Cannot manage users
    manageCampaignSync: false, // Cannot trigger campaign sync
    editableFields: ['call_feedback'], // Can ONLY edit call_feedback (audit logging enabled)
  },
  [USER_ROLES.IM]: {
    view: 'assigned_by_client',
    editWorkflowFields: true,
    createNotes: true,
    manageUsers: false,
    manageCampaignSync: false,
    editableFields: ['im_comment', 'priority', 'next_action', 'call_feedback', 'meeting_date', 'meeting_time', 'meeting_timezone'],
  },
  [USER_ROLES.SDR]: {
    view: 'assigned_by_client',
    editWorkflowFields: true,
    createNotes: true,
    manageUsers: false,
    manageCampaignSync: false,
    editableFields: ['sdr_status', 'sdr_last_contacted_at', 'sdr_comment', 'next_action', 'due_at', 'call_feedback', 'meeting_date', 'meeting_time', 'meeting_timezone'],
  },
  [USER_ROLES.AM]: {
    view: 'read_only_scoped',
    editWorkflowFields: false,
    createNotes: false,
    manageUsers: false,
    manageCampaignSync: false,
    editableFields: ['meeting_date', 'meeting_time', 'meeting_timezone', 'phone_override', 'to_name_override', 'company_name_override', 'website_override'],
  },
};

/**
 * Check if a role can edit a specific field
 * 
 * @param role - User role
 * @param fieldName - Field name to check
 * @returns true if role can edit the field
 */
export function canEditField(role: UserRole, fieldName: string): boolean {
  const permissions = PERMISSIONS[role];
  return permissions.editableFields?.includes(fieldName) ?? false;
}

/**
 * Check if a role has a specific permission
 * 
 * @param role - User role
 * @param permission - Permission key to check
 * @returns true if role has the permission
 */
export function hasPermission<K extends keyof PermissionMatrix>(
  role: UserRole,
  permission: K
): PermissionMatrix[K] {
  return PERMISSIONS[role][permission];
}